import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  useTheme,
  Typography,
  alpha,
  Card,
  CardContent,
  Chip,
  CircularProgress,
} from "@mui/material";
import { SearchOff, LocalOffer } from "@mui/icons-material";
import { toast } from "react-toastify";
import CategoryServices from "../../../services/CategoryService";
import SubCategoryServices from "../../../services/SubCategoryService";
import PublicMenuServices from "../../../services/PublicMenuService";
import OfferServices from "../../../services/OfferService";
import OrderItemServices from "../../../services/OrderItemService";
import {
  transformMenuData,
  extractOfferItems,
} from "../utils/menuDataTransform";
import { filterMenuItems } from "../utils/menuFilters";
import POSHeader from "./POSHeader";
import TableInfoSection from "./OrderPlacement/TableInfoSection";
import TableSwitcherDialog from "./OrderPlacement/TableSwitcherDialog";
import MenuItemCard from "./OrderPlacement/MenuItemCard";
import CartSummary from "./OrderPlacement/CartSummary";
import CategorySidebar from "./OrderPlacement/CategorySidebar";
import SubcategoryList from "./OrderPlacement/SubcategoryList";
import DietaryFilters from "./OrderPlacement/DietaryFilters";
import ItemCustomizationDialog from "./OrderPlacement/ItemCustomizationDialog";
import OrderCheckoutDialog from "./OrderCheckout/OrderCheckoutDialog";
import ImageLoader from "../../../components/ImageLoader/ImageLoader";
import { addItemToCart, calculateCartTotal } from "../utils/cartUtils";
import { useUserWithCompany } from "hooks/useCompany";
import { getFrequentlyBoughtItemsForMenuItem } from "state/frequentlyBoughtMap";

const OrderPlacementScreen = ({
  mainTab,
  setMainTab,
  isFullscreen,
  toggleFullscreen,
  fullscreenLabel,
  onPOSClick,
  handleTablesClick,
  handleOffersClick,
  onHomeClick,
  selectedTable,
  onBack,
  tables,
  onSelectTable,
  initialCart,
  existingOrderId,
  existingOrderNumber,
}) => {
  const { companyId } = useUserWithCompany();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState(initialCart || []);
  // Snapshot of original existing items (those already in the DB) for change detection
  const originalExistingItems = useRef(
    (initialCart || []).filter((i) => i.orderItemId),
  );

  // Pre-populate cart when initialCart is provided
  useEffect(() => {
    if (initialCart && initialCart.length > 0) {
      setCart(initialCart);
      originalExistingItems.current = initialCart.filter((i) => i.orderItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [showOffersOnly, setShowOffersOnly] = useState(false);
  const [isTableSwitcherOpen, setIsTableSwitcherOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [offerItems, setOfferItems] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [dietaryFilters, setDietaryFilters] = useState({
    isVegetarian: false,
    isVegan: false,
    isSpicy: false,
  });
  const [customizationItem, setCustomizationItem] = useState(null);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [addingItems, setAddingItems] = useState(false);

  // Handle saving changes to an existing order (POST new / PATCH modified / DELETE removed)
  const handleAddItemsToExistingOrder = async () => {
    if (!existingOrderId) return;
    try {
      setAddingItems(true);

      const originals = originalExistingItems.current;
      const originalIds = new Set(originals.map((i) => i.orderItemId));
      const currentExisting = cart.filter((i) => i.orderItemId);
      const currentExistingIds = new Set(
        currentExisting.map((i) => i.orderItemId),
      );

      // 1. DELETE items removed from cart
      for (const orig of originals) {
        if (!currentExistingIds.has(orig.orderItemId)) {
          await OrderItemServices.deleteOrderItem(orig.orderItemId, {
            companyId,
          });
        }
      }

      // 2. PATCH items whose quantity changed
      for (const cartItem of currentExisting) {
        const orig = originals.find(
          (o) => o.orderItemId === cartItem.orderItemId,
        );
        if (orig && orig.quantity !== cartItem.quantity) {
          const unitPrice = parseFloat(
            cartItem.finalPrice ?? cartItem.price ?? 0,
          );
          await OrderItemServices.updateOrderItem(cartItem.orderItemId, {
            companyId,
            quantity: cartItem.quantity,
            unitPrice,
            totalPrice: parseFloat((unitPrice * cartItem.quantity).toFixed(2)),
          });
        }
      }

      // 3. POST brand-new items (no orderItemId)
      const newItems = cart.filter((i) => !i.orderItemId);
      for (const cartItem of newItems) {
        const itemData = { companyId, orderId: existingOrderId };
        if (cartItem.isOffer) {
          itemData.offerId = cartItem.offerId;
          itemData.quantity = cartItem.quantity;
          itemData.unitPrice = parseFloat(cartItem.price ?? 0);
          itemData.totalPrice = parseFloat(
            (itemData.unitPrice * cartItem.quantity).toFixed(2),
          );
        } else {
          const unitPrice = parseFloat(
            cartItem.finalPrice ?? cartItem.price ?? 0,
          );
          itemData.menuItemId = cartItem.id;
          itemData.quantity = cartItem.quantity;
          itemData.unitPrice = unitPrice;
          itemData.totalPrice = parseFloat(
            (unitPrice * cartItem.quantity).toFixed(2),
          );
          if (cartItem.selectedVariation)
            itemData.variationId = cartItem.selectedVariation.id;
          if (cartItem.selectedModifiers?.length > 0)
            itemData.modifiers = cartItem.selectedModifiers.map((m) => m.id);
          if (cartItem.specialInstructions)
            itemData.specialInstructions = cartItem.specialInstructions;
        }
        await OrderItemServices.addOrderItem(itemData);
      }

      const deletedCount = [...originalIds].filter(
        (id) => !currentExistingIds.has(id),
      ).length;
      const updatedCount = currentExisting.filter((ci) => {
        const orig = originals.find((o) => o.orderItemId === ci.orderItemId);
        return orig && orig.quantity !== ci.quantity;
      }).length;

      if (newItems.length === 0 && deletedCount === 0 && updatedCount === 0) {
        toast.info("No changes were made to the order.");
      } else {
        toast.success("Order updated successfully!");
      }
      setCart([]);
      onBack();
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error(
        error.response?.data?.error ||
        "Failed to update order. Please try again.",
      );
    } finally {
      setAddingItems(false);
    }
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await CategoryServices.getAll({
          isActive: true,
          companyId: companyId,
        });
        if (response?.data?.view?.data) {
          // Add "all" category at the beginning
          const allCategory = {
            id: "all",
            name: "all",
            image: null,
          };
          const categoryData = [
            allCategory,
            ...response.data.view.data.sort(
              (a, b) => a.displayOrder - b.displayOrder,
            ),
          ];
          setCategories(categoryData);
        } else {
          setCategories([]);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };

    if (!companyId) return;
    fetchCategories();
  }, [companyId]);

  // Fetch subcategories from API based on selected category
  useEffect(() => {
    const fetchSubcategories = async () => {
      setLoadingSubcategories(true);
      try {
        const params = {
          isActive: true,
          companyId: companyId,
        };

        // If a specific category is selected (not "all"), filter by categoryId
        if (selectedCategory && selectedCategory !== "all") {
          params.categoryId = selectedCategory;
        }

        const response = await SubCategoryServices.getAll(params);
        if (response?.data?.view?.data) {
          // Add "all" subcategory at the beginning
          const allSubcategory = {
            id: "all",
            name: "all",
            image: null,
          };
          const subcategoryData = [
            allSubcategory,
            ...response.data.view.data.sort(
              (a, b) => a.displayOrder - b.displayOrder,
            ),
          ];
          setSubcategories(subcategoryData);
        } else {
          setSubcategories([]);
        }
      } catch (error) {
        console.error("Error fetching subcategories:", error);
        toast.error("Failed to load subcategories");
        setSubcategories([]);
      } finally {
        setLoadingSubcategories(false);
      }
    };

    if (!companyId) return;
    fetchSubcategories();
  }, [selectedCategory, companyId]);

  // Fetch menu items from API
  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const response = await PublicMenuServices.getAll({ companyId });
        if (response?.data?.view) {
          const transformedItems = transformMenuData(response.data.view);
          const transformedItemsWithInMemoryFBT = transformedItems.map((menuItem) => {
            const inMemoryRelatedIds = getFrequentlyBoughtItemsForMenuItem(
              menuItem.id,
            );

            return {
              ...menuItem,
              frequentlyBoughtItemIds:
                inMemoryRelatedIds || menuItem.frequentlyBoughtItemIds || [],
            };
          });
          const offers = extractOfferItems(response.data.view);
          setMenuItems(transformedItemsWithInMemoryFBT);
          setOfferItems(offers);
        } else {
          setMenuItems([]);
          setOfferItems([]);
        }
      } catch (error) {
        console.error("Error fetching menu items:", error);
        toast.error("Failed to load menu items");
        setMenuItems([]);
        setOfferItems([]);
      }
    };

    if (!companyId) return;
    fetchMenuItems();
  }, [companyId]);

  // Fetch offers from API
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        setLoadingOffers(true);
        const response = await OfferServices.getAll({ companyId });
        if (response?.data?.view?.data) {
          setOffers(response.data.view.data);
        } else {
          setOffers([]);
        }
      } catch (error) {
        console.error("Error fetching offers:", error);
        toast.error("Failed to load offers");
        setOffers([]);
      } finally {
        setLoadingOffers(false);
      }
    };

    if (!companyId) return;
    fetchOffers();
  }, [companyId]);

  const subcategoryNameById = useMemo(() => {
    const map = {};
    subcategories.forEach((sub) => {
      if (sub?.id && sub.id !== "all") {
        map[sub.id] = sub.name;
      }
    });
    return map;
  }, [subcategories]);

  // Get filtered items using memoization
  const filteredItems = useMemo(
    () =>
      filterMenuItems(
        menuItems,
        searchQuery,
        selectedCategory,
        selectedSubcategory,
        showOffersOnly,
        offerItems,
        dietaryFilters,
      ),
    [
      menuItems,
      searchQuery,
      selectedCategory,
      selectedSubcategory,
      showOffersOnly,
      offerItems,
      dietaryFilters,
    ],
  );

  // Handler for dietary filter changes
  const handleDietaryFilterChange = (filterKey) => {
    setDietaryFilters((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }));
  };

  // Cart handlers
  const handleAddToCart = (item) => {
    // Check if item has variations or modifiers
    const hasVariations = item.variations && item.variations.length > 0;
    const hasModifiers =
      item.modifierOptions && item.modifierOptions.length > 0;

    if (hasVariations || hasModifiers) {
      // Open customization dialog
      setCustomizationItem(item);
      setIsCustomizationOpen(true);
    } else {
      // Add directly to cart
      setCart((prevCart) => addItemToCart(prevCart, item));
    }
  };

  const handleAddOfferToCart = (offer) => {
    // Create offer cart item with the required structure
    const offerCartItem = {
      offerId: offer.id,
      quantity: 1,
      name: offer.name,
      price: parseFloat(offer.price),
      image: offer.image,
      description: offer.description,
      type: offer.type,
      itemCount: offer.OfferItems?.length || 0,
      isOffer: true, // Flag to identify offers in cart
    };

    // Check if offer already exists in cart
    setCart((prevCart) => {
      const existingOfferIndex = prevCart.findIndex(
        (item) => item.isOffer && item.offerId === offer.id,
      );

      if (existingOfferIndex !== -1) {
        // Increment quantity if already in cart
        const updatedCart = [...prevCart];
        updatedCart[existingOfferIndex] = {
          ...updatedCart[existingOfferIndex],
          quantity: updatedCart[existingOfferIndex].quantity + 1,
        };
        toast.success(`${offer.name} quantity increased`);
        return updatedCart;
      } else {
        // Add new offer to cart
        toast.success(`${offer.name} added to cart`);
        return [...prevCart, offerCartItem];
      }
    });
  };

  const handleAddCustomizedItemToCart = (customizedItem) => {
    setCart((prevCart) => addItemToCart(prevCart, customizedItem));
  };

  const handleGoToCartFromCustomization = () => {
    setIsCustomizationOpen(false);
    setCustomizationItem(null);
  };

  const handleIncrementCartItem = (itemId) => {
    setCart((prevCart) => {
      return prevCart.map((item) => {
        // For offers, check by offerId
        if (item.isOffer && item.offerId === itemId) {
          return { ...item, quantity: item.quantity + 1 };
        }
        // For regular menu items, match by cartItemId or id
        if (!item.isOffer && (item.cartItemId || item.id) === itemId) {
          return { ...item, quantity: item.quantity + 1 };
        }
        return item;
      });
    });
  };

  const handleRemoveFromCart = (itemId) => {
    setCart((prevCart) => {
      return prevCart
        .map((item) => {
          // For offers, check by offerId
          if (item.isOffer && item.offerId === itemId) {
            if (item.quantity > 1) {
              return { ...item, quantity: item.quantity - 1 };
            }
            return null; // Remove if quantity becomes 0
          }
          // For regular menu items, match by cartItemId or id
          if (!item.isOffer && (item.cartItemId || item.id) === itemId) {
            if (item.quantity > 1) {
              return { ...item, quantity: item.quantity - 1 };
            }
            return null;
          }
          return item;
        })
        .filter((item) => item !== null);
    });
  };

  const handleDeleteFromCart = (itemId) => {
    setCart((prevCart) => {
      return prevCart.filter((item) => {
        // For offers, check by offerId
        if (item.isOffer) {
          return item.offerId !== itemId;
        }
        // For regular menu items, match by cartItemId or id
        return (item.cartItemId || item.id) !== itemId;
      });
    });
  };

  const handlePlaceOrder = () => {
    if (existingOrderId) {
      handleAddItemsToExistingOrder();
    } else {
      setIsCheckoutOpen(true);
    }
  };

  const handleConfirmOrder = (orderData) => {
    // Clear cart after successful order (order is already placed in OrderCheckoutDialog)
    console.log("Order confirmed:", orderData);
    setCart([]);
    // Close checkout dialog
    setIsCheckoutOpen(false);
    // Navigate back to dashboard
    onBack();
  };

  return (
    <Box
      sx={{
        height: "100%",
        bgcolor: "#f5f5f5",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <POSHeader
        mainTab={mainTab}
        setMainTab={setMainTab}
        isFullscreen={isFullscreen}
        toggleFullscreen={toggleFullscreen}
        fullscreenLabel={fullscreenLabel}
        onPOSClick={onPOSClick}
        onTablesClick={handleTablesClick}
        onOffersClick={handleOffersClick}
        onHomeClick={onHomeClick}
      />

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          minHeight: 0, // Ensures proper flex shrinking
          position: "relative",
        }}
      >
        {/* Left Panel - Categories Sidebar */}
        <CategorySidebar
          categories={categories}
          selectedCategory={selectedCategory}
          onCategoryChange={(categoryId) => {
            setSelectedCategory(categoryId);
            setSelectedSubcategory("all");
            setShowOffersOnly(false);
          }}
          loading={loadingCategories}
        />

        {/* Middle Panel - Menu Items with Subcategories */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              p: 1.5,
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {/* Table Info & Back Button with Search */}
            <TableInfoSection
              selectedTable={selectedTable}
              onBack={onBack}
              searchQuery={searchQuery}
              onSearchChange={(e) => setSearchQuery(e.target.value)}
              onChangeTable={() => setIsTableSwitcherOpen(true)}
              showOffersOnly={showOffersOnly}
              onToggleOffers={() => setShowOffersOnly(!showOffersOnly)}
              offerCount={offers.length}
            />

            {/* Table Switcher Dialog */}
            <TableSwitcherDialog
              open={isTableSwitcherOpen}
              onClose={() => setIsTableSwitcherOpen(false)}
              tables={tables || []}
              selectedTable={selectedTable}
              onSelectTable={onSelectTable}
            />

            {/* Subcategories */}
            <SubcategoryList
              subcategories={subcategories}
              selectedSubcategory={selectedSubcategory}
              onSubcategoryChange={(subcategoryId) => {
                setSelectedSubcategory(subcategoryId);
                setShowOffersOnly(false);
                // Reset category to "all" when "all" subcategory is clicked
                if (subcategoryId === "all") {
                  setSelectedCategory("all");
                }
              }}
              selectedCategory={selectedCategory}
              loading={loadingSubcategories}
            />

            {/* Dietary Filters */}
            <DietaryFilters
              filters={dietaryFilters}
              onFilterChange={handleDietaryFilterChange}
            />

            {/* Menu Items / Offers Grid */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  bgcolor: "#F3F4F6",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: theme.palette.primary.main,
                  borderRadius: "10px",
                },
              }}
            >
              {showOffersOnly ? (
                // Offers View
                loadingOffers ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 8,
                    }}
                  >
                    <CircularProgress size={60} />
                  </Box>
                ) : offers.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      py: 8,
                    }}
                  >
                    <LocalOffer
                      sx={{
                        fontSize: 80,
                        color: alpha(theme.palette.error.main, 0.3),
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      No Offers Available
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.secondary, 0.7),
                        textAlign: "center",
                        maxWidth: 400,
                      }}
                    >
                      There are currently no active offers or promotions.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {offers.map((offer) => (
                      <Grid
                        key={offer.id}
                        size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                      >
                        <Card
                          elevation={0}
                          onClick={() => handleAddOfferToCart(offer)}
                          sx={{
                            cursor: "pointer",
                            height: "100%",
                            minHeight: 340,
                            display: "flex",
                            flexDirection: "column",
                            border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            transition: "all 0.2s ease",
                            position: "relative",
                            overflow: "visible",
                            "&:hover": {
                              transform: "translateY(-4px)",
                              boxShadow: `0 8px 20px ${alpha(theme.palette.error.main, 0.15)}`,
                            },
                          }}
                        >
                          <Chip
                            icon={<LocalOffer sx={{ fontSize: 16 }} />}
                            label="OFFER"
                            size="small"
                            sx={{
                              position: "absolute",
                              top: -12,
                              right: 8,
                              bgcolor: theme.palette.error.main,
                              color: "white",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              height: 24,
                              zIndex: 1,
                              "& .MuiChip-icon": {
                                color: "white",
                              },
                            }}
                          />
                          <CardContent
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: 140,
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                borderRadius: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 2,
                                overflow: "hidden",
                                position: "relative",
                              }}
                            >
                              {offer.image ? (
                                <ImageLoader
                                  imagePath={offer.image}
                                  width="100%"
                                  height={140}
                                  alt={offer.name}
                                  borderRadius={2}
                                />
                              ) : (
                                <Typography
                                  variant="h3"
                                  sx={{
                                    opacity: 0.2,
                                    fontWeight: 700,
                                  }}
                                >
                                  {offer.name.charAt(0)}
                                </Typography>
                              )}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  left: 8,
                                  display: "flex",
                                  gap: 0.5,
                                }}
                              >
                                <Chip
                                  label={offer.type.toUpperCase()}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.9,
                                    ),
                                    color: "white",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    height: 20,
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                mb: 0.5,
                                fontSize: "1rem",
                              }}
                            >
                              {offer.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {offer.description}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                pt: 1.5,
                                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    color: theme.palette.error.main,
                                    fontSize: "1.25rem",
                                  }}
                                >
                                  Rs. {parseFloat(offer.price).toFixed(2)}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "0.7rem" }}
                              >
                                {offer.OfferItems?.length || 0} item
                                {offer.OfferItems?.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )
              ) : // Menu Items View
                filteredItems.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      py: 8,
                    }}
                  >
                    <SearchOff
                      sx={{
                        fontSize: 80,
                        color: alpha(theme.palette.text.secondary, 0.3),
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      No Items Found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.text.secondary, 0.7),
                        textAlign: "center",
                        maxWidth: 400,
                      }}
                    >
                      {searchQuery
                        ? `No items match "${searchQuery}"`
                        : selectedCategory !== "all" ||
                          selectedSubcategory !== "all"
                          ? "No items available in this category"
                          : "No items available"}
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2} alignItems="flex-start">
                    {filteredItems.map((item) => (
                      <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                        <MenuItemCard
                          item={item}
                          onAddToCart={handleAddToCart}
                          subcategoryNameById={subcategoryNameById}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
            </Box>
          </Box>
        </Box>

        {/* Right Side - Cart (stays visible while item customization overlay is open) */}
        <Box
          sx={{
            position: "relative",
            flexShrink: 0,
            zIndex: isCustomizationOpen ? theme.zIndex.modal + 1 : "auto",
          }}
        >
          <CartSummary
            cart={cart}
            onAddToCart={handleIncrementCartItem}
            onRemoveFromCart={handleRemoveFromCart}
            onDeleteFromCart={handleDeleteFromCart}
            calculateTotal={() => calculateCartTotal(cart)}
            onPlaceOrder={handlePlaceOrder}
            buttonLabel={
              existingOrderId
                ? addingItems
                  ? "Saving..."
                  : "Save Changes"
                : "Place Order"
            }
            addingModeLabel={
              existingOrderId
                ? `Adding items to Order${existingOrderNumber ? ` #${existingOrderNumber}` : ""}`
                : null
            }
          />
        </Box>
      </Box>

      {/* Item Customization Dialog */}
      <ItemCustomizationDialog
        open={isCustomizationOpen}
        item={customizationItem}
        onClose={() => {
          setIsCustomizationOpen(false);
          setCustomizationItem(null);
        }}
        onAddToCart={handleAddCustomizedItemToCart}
        allMenuItems={menuItems}
        onAddMenuItem={handleAddToCart}
        onAddOfferToCart={handleAddOfferToCart}
        cartItemCount={cart.reduce((sum, cartItem) => sum + (cartItem.quantity || 1), 0)}
        onGoToCart={handleGoToCartFromCustomization}
      />

      {/* Order Checkout Dialog */}
      <OrderCheckoutDialog
        open={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        table={selectedTable}
        cart={cart}
        total={calculateCartTotal(cart)}
        onConfirmOrder={handleConfirmOrder}
      />
    </Box>
  );
};

export default OrderPlacementScreen;
